import { CdkAccordion } from '@angular/cdk/accordion';
import { DatePipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, computed, effect, inject, signal, untracked, ViewContainerRef } from '@angular/core';
import { MatChip } from '@angular/material/chips';
import { MatDialog } from '@angular/material/dialog';
import { PageEvent } from '@angular/material/paginator';
import { MatTooltip } from '@angular/material/tooltip';
import { RouterLink } from '@angular/router';
import { lastValueFrom } from 'rxjs';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { ENVIRONMENT } from '../../../../../environments/environment.interface';
import { Assignment, AssignmentWithRelations } from '../../../../api/app-types/assignment';
import { Client, RestrictedClient } from '../../../../api/app-types/client.app';
import { Lek } from '../../../../api/app-types/lek.app';
import { PersonType } from '../../../../api/app-types/person-type.app';
import { DB_PHASES } from '../../../../api/app-types/phase.app';
import { REPORT_TYPE, ReportDocument } from '../../../../api/app-types/report-document.app';
import { User } from '../../../../api/app-types/user.app';
import { isResetToPhaseOneDecision, ResetToPhaseOneDecision } from '../../../../api/api-types/reset-to-phase-one.api';
import { ASSIGNMENT_API } from '../../../../api/services/assignment.service';
import { LEK_PROVIDER } from '../../../../api/services/lek.service';
import { REPORT_API } from '../../../../api/services/report.service';
import { ActionMenuItem, LvrActionMenu } from '../../../../core/components/action-menu/action-menu';
import { LvrAssignmentAccordionItem } from '../../../../core/components/assignment-accordion-item/assignment-accordion-item';
import { AssignmentDetails } from '../../../../core/components/assignment-details/assignment-details';
import { LvrHeading } from '../../../../core/components/heading/heading';
import { LvrButton } from '../../../../core/components/lvr-button/lvr-button';
import { ResetToPhaseOneDialog } from './reset-to-phase-one-dialog';
import { Paginator } from '../../../../core/components/paginator/paginator';
import { AccessService } from '../../../../core/services/access.service';
import { LOADING } from '../../../../core/services/loading.service';
import { RECORD_MANAGER } from '../../../../core/services/record.provide';
import { REPRESENTATION_MODE } from '../../../../core/services/representation-mode.service';
import { STATE } from '../../../../core/services/state.service';
import { downloadDocumentFiles } from '../../../../shared/helpers/document-download.func';
import { NOTIFICATION_SERVICE } from '../../../../shared/notification/notification';
import { LvrPaddingDirective } from '../../../../shared/padding/padding.directive';
import { PaginatedResponse } from '../../../../shared/pagination/paginated-response';
import { hasPermission } from '../../../../shared/permissions/permission.func';
import { ClientResolved } from '../../../../shared/routing/resolvers/client.resolver';
import { RouteInjection } from '../../../../shared/routing/route-injection.class';
import { SessionManagerService } from '../../../../shared/storage/session-manager.service';
import { NavCarousel } from '../../../../core/components/nav-carousel/nav-carousel';
import { TENANT_FEATURES } from '../../../../core/services/tenant-feature.service';

export const ADMISSION_QB_TEMPLATE_ID = 28;
export const END_QB_TEMPLATE_ID = 29;

@Component({
	selector: 'lvr-assignment-view',
	templateUrl: './assignment-view.html',
	styleUrl: './assignment-view.scss',
	imports: [
		CdkAccordion,
		DatePipe,
		LvrHeading,
		TranslocoPipe,
		LvrActionMenu,
		LvrPaddingDirective,
		LvrAssignmentAccordionItem,
		DatePipe,
		RouterLink,
		MatChip,
		MatTooltip,
		LvrButton,
		Paginator,
		AssignmentDetails,
		NavCarousel,
	],
})
export class AssignmentView extends RouteInjection {
	private readonly assignmentApi = inject(ASSIGNMENT_API);
	private readonly viewContainerRef = inject(ViewContainerRef);
	private readonly state = inject(STATE);
	private readonly dialog = inject(MatDialog);
	private readonly loading = inject(LOADING);
	private readonly api = inject(REPORT_API);
	private readonly environment = inject(ENVIRONMENT);
	private readonly lekApi = inject(LEK_PROVIDER);
	private readonly ntfy = inject(NOTIFICATION_SERVICE);
	private readonly access = inject(AccessService);
	private readonly representationMode = inject(REPRESENTATION_MODE);
	private readonly recordManager = inject(RECORD_MANAGER);
	private readonly session = inject(SessionManagerService);
	private readonly tenantFeatures = inject(TENANT_FEATURES);
	private readonly transloco = inject(TranslocoService);
	private readonly additionalReportsEnabled = this.tenantFeatures.additionalReportsEnabled;
	private readonly resolvedReportsPage = this.resolveDataAsSignal<PaginatedResponse<ReportDocument>>('reports');
	private readonly refreshedReportsPage = signal<PaginatedResponse<ReportDocument> | undefined>(undefined);
	private readonly reportsPage = computed(() => this.refreshedReportsPage() ?? this.resolvedReportsPage());
	private readonly initialLekPage = this.resolveDataAsSignal<PaginatedResponse<Lek>>('lekPage');
	private readonly client = this.resolveDataAsSignal<ClientResolved>('client');
	public readonly clientId = computed(() => this.client()?.value.id ?? 0);
	private readonly user = this.resolveDataAsSignal<User>('user');
	private readonly resolvedAssignment = this.resolveDataAsSignal<AssignmentWithRelations>('assignment');
	private readonly refreshedAssignment = signal<AssignmentWithRelations | undefined>(undefined);
	public readonly resetStatus = signal<'hidden' | 'posting' | 'refreshing' | 'error' | 'unavailable'>('hidden');
	private resetGeneration = 0;
	private resetKey = '';
	private dialogOpen = false;
	private readonly postMayHaveSucceeded = signal(false);
	private readonly resetPermission = hasPermission(this.session.permissions, ['Ifd.All', 'Ifd.Own']);
	private readonly transitionPermission = hasPermission(this.session.permissions, 'Phases.Transition');
	public readonly canShowReset = computed(() => {
		const assignment = this.assignment();
		return (
			!!assignment &&
			!assignment.isAdditionalAssignment &&
			!assignment.isServiceOfficeStatement &&
			(assignment.phaseId === DB_PHASES.P2 || assignment.phaseId === DB_PHASES.P3) &&
			!this.representationMode.isActive() &&
			this.resetPermission() &&
			this.transitionPermission() &&
			this.user()?.roles?.shortName?.toLowerCase() === 'admin' &&
			Number.isInteger(this.clientId()) &&
			this.clientId() > 0 &&
			Number.isInteger(assignment.id) &&
			assignment.id > 0
		);
	});
	public readonly hideResetDetails = computed(
		() =>
			this.resetStatus() === 'posting' ||
			this.resetStatus() === 'refreshing' ||
			this.resetStatus() === 'unavailable' ||
			(this.resetStatus() === 'error' && this.postMayHaveSucceeded()),
	);
	private readonly initiatorsPage = this.resolveDataAsSignal<PaginatedResponse<PersonType>>('initiatorsPage');
	public readonly initiators = computed(() => this.initiatorsPage()?.entities ?? []);
	private readonly lastTitle = signal<string | null>(null);
	public readonly hideHeading = signal(true);
	private readonly lekPagerStateByKey = signal<Record<string, { pageIndex: number; pageSize: number }>>({});
	private lekPageRequestCounter = 0;
	private readonly emptyLekPage: PaginatedResponse<Lek> = {
		totalEntities: 0,
		range: this.environment.defaultPageSize,
		offset: 0,
		entities: [],
	};
	public readonly lekPage = signal<PaginatedResponse<Lek>>(this.emptyLekPage);
	public readonly backLink = computed(() =>
		this.router.serializeUrl(this.router.createUrlTree(['../'], { relativeTo: this.route })),
	);
	public readonly carouselElements = computed(() => {
		const assignment = this.assignment();
		const id = String(assignment?.id ?? '');

		return [
			{
				id,
				label: assignment?.phase?.remark ?? id,
			},
		];
	});

	public readonly leks = computed(() => this.lekPage()?.entities ?? []);
	public readonly PHASE3 = DB_PHASES.P3;
	public readonly PHASE4 = DB_PHASES.P4;
	public readonly assignment = computed(() => this.refreshedAssignment() ?? this.resolvedAssignment());
	public readonly canEditAssignmentPermission = hasPermission(this.session.permissions, 'Assignments.Edit');

	public readonly canEditAssignment = computed(() => {
		const assignment = this.assignment();
		const user = this.user();
		const client = this.client();

		if (!assignment || !user) return false;
		if (!this.canEditAssignmentPermission()) return false;

		if (assignment.isAdditionalAssignment) {
			return (
				(this.access.isAdditionalOwner(assignment, user) && !this.representationMode.isActive()) ||
				(this.representationMode.isActive() && this.isRepresentingAdditionalAssignmentOwner(assignment))
			);
		}

		if (client?.kind === 'restricted') {
			return false;
		}

		return (
			(this.access.isMainOwner(assignment, user) && !this.representationMode.isActive()) ||
			(this.representationMode.isActive() && this.isRepresentingAssignmentOwner(assignment))
		);
	});

	private isRepresentingUser(userId?: number | string | null): boolean {
		const representedUserId = this.representationMode.mode().representedUserId;

		if (representedUserId == null || userId == null) return false;

		return String(representedUserId) === String(userId);
	}

	private isRepresentingAssignmentOwner(assignment: Assignment): boolean {
		return this.isRepresentingUser(assignment.createdByUser?.id);
	}

	private isRepresentingAdditionalAssignmentOwner(assignment: Assignment): boolean {
		const owner = assignment.ownerOfAdditionalAssignment ?? assignment.createdByUser;

		return this.isRepresentingUser(owner?.id);
	}

	public readonly canEditAssignmentPhaseOneOnlyPermission = hasPermission(
		this.session.permissions,
		'Assignments.Edit.PhaseOneOnly',
	);
	public readonly isNotP4 = computed(() => this.assignment()?.phaseId !== DB_PHASES.P4);

	private readonly canReadAssignmentPermission = hasPermission(this.session.permissions, [
		'Assignments.Read',
		'Assignments.Read.PhaseOneOnly',
	]);
	private readonly canCreateReportsPermission = hasPermission(this.session.permissions, 'Reports.Create');
	private readonly canCreateLekPermission = hasPermission(this.session.permissions, 'AssistanceResults.Create');
	public readonly canReadReportsPermission = hasPermission(this.session.permissions, 'Reports.Read');
	public readonly canEditReportsPermission = hasPermission(this.session.permissions, 'Reports.Edit');
	public readonly canCreateReports = computed(
		() => this.canCreateReportsPermission() && (this.canEditAssignment() || this.representationMode.isActive()),
	);
	public readonly canSendAssignment = computed(() => this.canReadAssignmentPermission());
	public readonly canCreateLekAction = computed(() => this.canEditAssignment() && this.canCreateLekPermission());
	public readonly showFirstLastButtons = true;
	public readonly hidePageSize = true;

	public readonly reports = computed(() =>
		(this.reportsPage()?.entities ?? []).filter((r) => r.categoryReport.id !== REPORT_TYPE.TECHNICAL_START_REPORT),
	);
	public readonly shouldBeAbleToCreateLek = computed(() => this.assignment()?.phase.remark?.toLowerCase() === 'p3');
	public readonly contactPhaseStart = computed(() => {
		const a = this.assignment()!;
		if (a.contactStart) return a.contactStart;
		if (a.contacts.length > 0) {
			const sortedContacts = [...a.contacts].sort((c1, c2) => c1.contactedAt.getTime() - c2.contactedAt.getTime());
			return sortedContacts[0].contactedAt;
		}
		return undefined;
	});

	public readonly canCreateStartReportInPhase = computed(
		() =>
			(this.assignment()?.phaseId === DB_PHASES.P2 && this.assignment()?.phaseChangeId !== DB_PHASES.P4) ||
			(this.assignment()?.phaseId === DB_PHASES.P1 && this.assignment()?.draftPhaseId === DB_PHASES.P3),
	);

	public readonly canCreateAdmissionQBReportInPhase = computed(
		() =>
			this.assignment()?.phaseId === DB_PHASES.P1 &&
			(!this.assignment()?.draftPhaseId || this.assignment()?.draftPhaseId === DB_PHASES.P2),
	);

	public readonly canCreateEndQBReportInPhase = computed(
		() =>
			this.assignment()?.phaseId === DB_PHASES.P2 &&
			(!this.assignment()?.phaseChangeId || this.assignment()?.phaseChangeId === DB_PHASES.P4),
	);

	private readonly loadingLabel = 'base.button.creating_report';
	private readonly showLoadingStartReport = this.loading.isCurrentlyLoading(
		'assignment-view.routeToStartReport',
		'requestKey',
	);
	private readonly showLoadingEndReport = this.loading.isCurrentlyLoading(
		'assignment-view.routeToEndReport',
		'requestKey',
	);
	private readonly showLoadingImpedimentReport = this.loading.isCurrentlyLoading(
		'assignment-view.routeToImpedimentReport',
		'requestKey',
	);
	private readonly showLoadingAdmissionQbReport = this.loading.isCurrentlyLoading(
		'assignment-view.routeToStartQbReport',
		'requestKey',
	);
	private readonly showLoadingEndQbReport = this.loading.isCurrentlyLoading(
		'assignment-view.routeToEndQbReport',
		'requestKey',
	);

	public readonly menuItems = computed<ActionMenuItem[]>(() => {
		return [
			{
				label: 'Anfangsbericht',
				onClick: () => this.routeToStartReport(),
				icon: 'add',
				disabled:
					!this.canCreateReports() ||
					this.reports().some((r) => r.categoryReport.id === REPORT_TYPE.START_REPORT) ||
					// "Anfangsbericht" can only be created in P2 and draft P3
					!this.canCreateStartReportInPhase(),
				showLoading: this.showLoadingStartReport(),
				loadingLabel: this.loadingLabel,
			},

			...(this.additionalReportsEnabled()
				? [
						{
							// AufnahmeQB - Anfangsbericht für P2
							label: 'Aufnahme QBS',
							onClick: () => this.routeToStartQBReport(),
							icon: 'add',
							disabled:
								!this.canCreateReports() ||
								this.reports().some((r) => r.categoryReport.id === REPORT_TYPE.ADMISSION_QB_REPORT) ||
								!this.canCreateAdmissionQBReportInPhase(),
							showLoading: this.showLoadingAdmissionQbReport(),
							loadingLabel: this.loadingLabel,
						},
					]
				: []),
			{
				label: 'Sachstandsbericht',
				onClick: () => this.routeToImpedimentReport(),
				icon: 'add',
				disabled: !this.canCreateReports() || this.assignment()?.phaseId !== DB_PHASES.P3,
				loadingLabel: this.loadingLabel,
				showLoading: this.showLoadingImpedimentReport(),
			},
			{
				label: 'Abschlussbericht',
				onClick: () => this.routeToEndReport(),
				icon: 'add',
				disabled:
					!this.canCreateReports() ||
					this.assignment()?.phaseId !== DB_PHASES.P3 ||
					this.reports().some((r) => r.categoryReport.id === REPORT_TYPE.END_REPORT),
				showLoading: this.showLoadingEndReport(),
				loadingLabel: this.loadingLabel,
			},
			...(this.additionalReportsEnabled()
				? [
						{
							// AbschlussQB (Abschlussbericht für P2 zu P4)
							label: 'Abschluss QBS',
							onClick: () => this.routeToEndQBReport(),
							icon: 'add',
							disabled:
								!this.canCreateReports() ||
								this.reports().some((r) => r.categoryReport.id === REPORT_TYPE.END_QB_REPORT) ||
								!this.canCreateEndQBReportInPhase(),
							showLoading: this.showLoadingEndQbReport(),
							loadingLabel: this.loadingLabel,
						},
					]
				: []),
		];
	});

	private readonly recordTitle = computed(() => {
		const c = this.client();
		const v = c?.value as Client | RestrictedClient;
		const name = v?.name?.trim() ?? '';
		const firstName = v?.firstName?.trim() ?? '';
		if (name && firstName) return `${name}, ${firstName}`;
		if (name) return name;
		if (firstName) return firstName;
		return 'Klient';
	});

	public constructor() {
		super();
		effect(() => {
			const clientId = this.clientId();
			const assignmentId = this.resolvedAssignment()?.id;
			const key = `${clientId}:${assignmentId}`;
			const allowed = this.canShowReset();
			if (key !== this.resetKey) {
				this.resetKey = key;
				this.resetGeneration++;
				this.refreshedAssignment.set(undefined);
				this.refreshedReportsPage.set(undefined);
				this.postMayHaveSucceeded.set(false);
				this.resetStatus.set('hidden');
			}
			if (!allowed) {
				this.resetGeneration++;
				this.resetStatus.set('hidden');
				return;
			}
		});
		effect(() => {
			const assignmentId = this.assignment()?.id;
			if (!assignmentId) {
				this.lekPage.set(this.emptyLekPage);
				return;
			}

			const key = this.lekStateKey(assignmentId);
			const persistedState = untracked(() => this.lekPagerStateByKey()[key]);

			if (persistedState) {
				void this.loadLekPage(assignmentId, persistedState.pageSize, persistedState.pageIndex, key);
				return;
			}

			const initialPage = this.initialLekPage() ?? this.emptyLekPage;
			this.lekPage.set(initialPage);

			untracked(() => {
				this.lekPagerStateByKey.update((s) => ({
					...s,
					[key]: {
						pageIndex: initialPage.offset ?? 0,
						pageSize: initialPage.range ?? this.environment.defaultPageSize,
					},
				}));
			});
		});

		effect(() => {
			const title = this.recordTitle();
			if (this.lastTitle() === title) return;
			// To avoid effect loop
			untracked(() => this.recordManager.updateRecordTitleOfActiveRecord(title));
			this.lastTitle.set(title);
		});
	}

	private currentResetKey(): string {
		return `${this.clientId()}:${this.resolvedAssignment()?.id}`;
	}

	public async confirmReset(blockedDecision?: ResetToPhaseOneDecision): Promise<void> {
		if (
			!this.canShowReset() ||
			this.dialogOpen ||
			this.resetStatus() === 'posting' ||
			this.resetStatus() === 'refreshing' ||
			this.postMayHaveSucceeded()
		)
			return;
		const key = this.currentResetKey();
		const generation = ++this.resetGeneration;
		let nextDecision: ResetToPhaseOneDecision | undefined;
		let resetSucceeded = false;
		this.dialogOpen = true;
		try {
			const confirmed = await lastValueFrom(
				this.dialog
					.open(ResetToPhaseOneDialog, {
						width: '480px',
						viewContainerRef: this.viewContainerRef,
						panelClass: 'reset-to-phase-one-confirmation',
						data: {
							clientId: this.clientId(),
							assignmentId: this.resolvedAssignment()!.id,
							blockedDecision,
						},
					})
					.afterClosed(),
			);
			if (!confirmed || generation !== this.resetGeneration || key !== this.currentResetKey() || !this.canShowReset())
				return;
			this.resetStatus.set('posting');
			try {
				await this.assignmentApi.resetToPhaseOne(this.clientId(), this.resolvedAssignment()!.id);
				if (generation !== this.resetGeneration || key !== this.currentResetKey()) return;
				this.postMayHaveSucceeded.set(true);
				resetSucceeded = true;
			} catch (error) {
				if (generation !== this.resetGeneration || key !== this.currentResetKey()) return;
				if (
					error instanceof HttpErrorResponse &&
					error.status === 400 &&
					isResetToPhaseOneDecision(error.error) &&
					!error.error.canReset
				) {
					this.resetStatus.set('hidden');
					nextDecision = error.error;
				} else {
					this.postMayHaveSucceeded.set(true);
					await this.handleResetError(error, key, generation);
				}
			}
			if (resetSucceeded && generation === this.resetGeneration && key === this.currentResetKey()) {
				await this.router.navigate(['/']);
			}
		} finally {
			this.dialogOpen = false;
			if (nextDecision && generation === this.resetGeneration && key === this.currentResetKey()) {
				void this.confirmReset(nextDecision);
			}
		}
	}

	private async refreshResetDetails(key: string, generation: number): Promise<void> {
		this.resetStatus.set('refreshing');
		const clientId = this.clientId();
		const assignmentId = this.resolvedAssignment()!.id;
		try {
			const [detail, reports, leks] = await Promise.all([
				this.assignmentApi.single(clientId, assignmentId),
				this.api.getDocumentsOfAssignment(assignmentId),
				this.lekApi.getAllByAssignment(assignmentId, this.lekPageSize(), this.lekPageIndex()),
				this.state.updateAssignments(),
			]);
			if (generation !== this.resetGeneration || key !== this.currentResetKey()) return;
			if (!detail.data) {
				this.resetStatus.set('unavailable');
				return;
			}
			this.refreshedAssignment.set(detail.data);
			this.refreshedReportsPage.set(reports);
			this.lekPage.set(leks);
			this.postMayHaveSucceeded.set(false);
			this.resetStatus.set('hidden');
		} catch (error) {
			if (generation !== this.resetGeneration || key !== this.currentResetKey()) return;
			this.resetStatus.set(error instanceof HttpErrorResponse && error.status === 404 ? 'unavailable' : 'error');
		}
	}

	private async handleResetError(error: unknown, key: string, generation: number): Promise<void> {
		if (error instanceof HttpErrorResponse && error.status === 404) {
			await this.refreshResetDetails(key, generation);
			if (generation === this.resetGeneration && key === this.currentResetKey()) this.resetStatus.set('unavailable');
			return;
		}
		this.resetStatus.set('error');
	}

	public routeBasedOnReportCategory(report: ReportDocument) {
		if (!this.canReadReportsPermission()) return;
		if ((report.documents ?? []).length > 0) {
			downloadDocumentFiles(report.documents ?? []);
			return;
		}

		const reportType = report.categoryReport.id;

		if (reportType === REPORT_TYPE.IMPEDIMENT_REPORT) {
			this.router.navigate(['report', 'impediment', report.id], {
				relativeTo: this.route,
				queryParams: {
					dueDate: this.assignment()?.impedimentReportDueDate?.getISODateStringWithoutTime(),
				},
			});
		} else if (reportType === REPORT_TYPE.END_REPORT) {
			this.router.navigate(['end-report', report.id], {
				relativeTo: this.route,
			});
		} else if (reportType === REPORT_TYPE.ADMISSION_QB_REPORT) {
			this.router.navigate(['admission-qb-report', report.id], {
				relativeTo: this.route,
			});
		} else if (reportType === REPORT_TYPE.END_QB_REPORT) {
			this.router.navigate(['end-qb-report', report.id], {
				relativeTo: this.route,
			});
		} else {
			this.router.navigate(['report', report.id], {
				relativeTo: this.route,
			});
		}
	}

	public openEdit() {
		if (!(this.canEditAssignment() || this.canEditAssignmentPhaseOneOnlyPermission())) return;
		this.router.navigate(['edit'], { relativeTo: this.route });
	}

	public openCreateLek() {
		if (!this.canCreateLekAction() || !this.shouldBeAbleToCreateLek()) return;
		this.router.navigate(['lek', 'add'], { relativeTo: this.route });
	}

	public lekPageIndex() {
		const assignmentId = this.assignment()?.id;
		if (!assignmentId) return 0;
		return this.lekPagerStateByKey()[this.lekStateKey(assignmentId)]?.pageIndex ?? 0;
	}

	public lekPageSize() {
		const assignmentId = this.assignment()?.id;
		if (!assignmentId) return this.environment.defaultPageSize;
		return this.lekPagerStateByKey()[this.lekStateKey(assignmentId)]?.pageSize ?? this.environment.defaultPageSize;
	}

	public async lekPageChanged(event: PageEvent) {
		const assignmentId = this.assignment()?.id;
		if (!assignmentId) return;

		const key = this.lekStateKey(assignmentId);
		this.lekPagerStateByKey.update((s) => ({
			...s,
			[key]: { pageIndex: event.pageIndex, pageSize: event.pageSize },
		}));

		await this.loadLekPage(assignmentId, event.pageSize, event.pageIndex, key);
	}

	private lekStateKey(assignmentId: number): string {
		return `${this.clientId()}:${assignmentId}`;
	}

	private async loadLekPage(assignmentId: number, pageSize: number, pageIndex: number, key: string): Promise<void> {
		const requestId = ++this.lekPageRequestCounter;
		const page = await this.lekApi.getAllByAssignment(assignmentId, pageSize, pageIndex);

		if (requestId !== this.lekPageRequestCounter) return;
		if (key !== this.lekStateKey(assignmentId)) return;

		this.lekPage.set(page);
	}

	private openNotification(msg: string) {
		this.ntfy.addNotification({
			message: msg,
			id: window.crypto.randomUUID(),
			type: 'error',
			showToast: true,
			timestamp: new Date(),
		});
	}

	private async routeToStartReport() {
		if (this.showLoadingStartReport()) return;

		if (!this.assignment()!.typeOfCareId) {
			this.openNotification('Dem Auftrag fehlt die Angabe der Betreuungsart.');
			return;
		}
		const templates = await this.api.getDocumentTemplates(this.assignment()!.typeOfCareId!);
		const template = templates?.entities.find((t) => {
			const categoryId = t.category?.id;
			if (!categoryId) {
				console.error('No template category ID');
				return;
			}
			return categoryId === REPORT_TYPE.START_REPORT;
		});
		if (!template) {
			this.openNotification('Es sind keine Berichtsvorlagen für diese Betreuungsart der Zuweisung hinterlegt.');
			return;
		}
		const uuid = this.loading.setLoadingState(window.crypto.randomUUID(), 'assignment-view.routeToStartReport');
		try {
			const result = await this.api.createReportDocument(template.id, this.assignment()!.id);
			if (!result)
				return this.openNotification('Der Anfangsbericht konnte nicht erstellt werden. Bitte versuchen Sie es erneut.');
			await this.router.navigate(['report', result], {
				relativeTo: this.route,
			});
		} finally {
			this.loading.clearLoadingState(uuid);
		}
	}

	private async routeToEndReport() {
		if (this.showLoadingEndReport()) return;

		if (!this.assignment()!.typeOfCareId) {
			this.openNotification('Dem Auftrag fehlt die Angabe der Betreuungsart.');
			return;
		}
		const templates = await this.api.getDocumentTemplates(this.assignment()!.typeOfCareId!);
		const template = templates?.entities.find((t) => {
			const categoryId = t.category?.id;
			if (!categoryId) {
				console.error('No category ID');
				return;
			}
			return categoryId === REPORT_TYPE.END_REPORT;
		});
		if (!template) {
			this.openNotification('Es sind keine Berichtsvorlagen für diese Betreuungsart der Zuweisung hinterlegt.');
			return;
		}
		const uuid = this.loading.setLoadingState(window.crypto.randomUUID(), 'assignment-view.routeToEndReport');
		try {
			const result = await this.api.createReportDocument(template.id, this.assignment()!.id);
			if (!result)
				return this.openNotification(
					'Der Abschlussbericht konnte nicht erstellt werden. Bitte versuchen Sie es erneut.',
				);
			await this.router.navigate(['end-report', result], {
				relativeTo: this.route,
			});
		} finally {
			this.loading.clearLoadingState(uuid);
		}
	}

	private async routeToImpedimentReport() {
		if (this.showLoadingImpedimentReport()) return;

		const uuid = this.loading.setLoadingState(window.crypto.randomUUID(), 'assignment-view.routeToImpedimentReport');
		try {
			const templates = await this.api.getDocumentTemplates();
			const impedimentTemplate = templates?.entities.find((t) => {
				const categoryId = t.category?.id;
				if (!categoryId) {
					console.error('No category ID');
					return;
				}
				return categoryId === REPORT_TYPE.IMPEDIMENT_REPORT;
			});
			if (!this.assignment()?.legalFoundationId) {
				this.openNotification('Dem Auftrag fehlt eine Grundlage. Bitte weisen Sie zuerst eine Grundlage zu.');
				return;
			}
			if (!impedimentTemplate) {
				this.openNotification('Es sind keine Berichtsvorlagen für den Sachstandsbericht hinterlegt.');
				return;
			}
			const result = await this.api.createReportDocument(impedimentTemplate.id, this.assignment()!.id);
			await this.router.navigate(['report', 'impediment', result], {
				relativeTo: this.route,
				queryParams: {
					dueDate: this.assignment()?.impedimentReportDueDate?.getISODateStringWithoutTime(),
				},
			});
		} finally {
			this.loading.clearLoadingState(uuid);
		}
	}

	private async routeToStartQBReport() {
		if (this.showLoadingAdmissionQbReport()) return;

		const templateId = ADMISSION_QB_TEMPLATE_ID;
		const uuid = this.loading.setLoadingState(window.crypto.randomUUID(), 'assignment-view.routeToAdmissionQbReport');
		try {
			const result = await this.api.createReportDocument(templateId, this.assignment()!.id);
			if (!result)
				return this.openNotification(
					'Der AufnahmeQB-Bericht konnte nicht erstellt werden. Bitte versuchen Sie es erneut.',
				);
			await this.router.navigate(['admission-qb-report', result], {
				relativeTo: this.route,
			});
		} finally {
			this.loading.clearLoadingState(uuid);
		}
	}

	private async routeToEndQBReport() {
		if (this.showLoadingEndQbReport()) return;

		const templateId = END_QB_TEMPLATE_ID;
		const uuid = this.loading.setLoadingState(window.crypto.randomUUID(), 'assignment-view.routeToEndQbReport');
		try {
			const result = await this.api.createReportDocument(templateId, this.assignment()!.id);
			if (!result)
				return this.openNotification(
					'Der AbschlussQB-Bericht konnte nicht erstellt werden. Bitte versuchen Sie es erneut.',
				);
			await this.router.navigate(['end-qb-report', result], {
				relativeTo: this.route,
			});
		} finally {
			this.loading.clearLoadingState(uuid);
		}
	}

	public openMail(recipient: string) {
		const assignmentGuid = this.assignment()?.guid ?? '-';
		const url = encodeURI(window.location.href);
		const body = `Auftrag Nummer: ${assignmentGuid}\nLink: ${url}`;
		const href = this.buildMailto(recipient, `KlifdWeb: Auftrag - Nummer: ${assignmentGuid}`, body);
		window.location.href = href;
	}

	public lekInstitutionId(lek: Lek): number | null {
		return (
			lek.workplace?.institutionBranch?.institutionId ??
			lek.workplace?.workplacesJobs?.[0]?.institutionBranch?.institutionId ??
			null
		);
	}

	public lekInstitutionName(lek: Lek): string | null {
		return (
			lek.workplace?.institutionBranch?.name ??
			lek.workplace?.workplacesJobs?.[0]?.institutionBranch?.name ??
			lek.workplace?.name ??
			null
		);
	}

	/**
	 * Builds a safe mailto U with properly encoded subject and body.
	 * Encodes special characters to prevent URL breakage (spaces, &, ?, etc.)
	 */
	private buildMailto(recipient: string, subject: string, body: string): string {
		const encoded = (s: string) => encodeURIComponent(s ?? '');
		const to = encodeURIComponent(recipient);
		return `mailto:${to}?subject=${encoded(subject)}&body=${encoded(body)}`;
	}
}
